import { useEffect, useRef, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { ExternalLink, Loader2, LogOut } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";
import { LogoMark } from "../components/Logo";
import api from "./adminApi";
import { toast } from "sonner";

const POLL_MS = 30_000;

const Badge = ({ n }) =>
  n > 0 ? (
    <span
      data-testid="admin-tab-badge"
      className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white ring-2 ring-black"
    >
      {n > 99 ? "99+" : n}
    </span>
  ) : null;

export default function AdminLayout() {
  const { user, logout } = useAdminAuth();
  const location = useLocation();
  const [stats, setStats] = useState({ applications_new: 0, newsletter_new: 0 });
  const lastRef = useRef({ applications_new: 0, newsletter_new: 0 });
  const firstLoadRef = useRef(true);

  useEffect(() => {
    if (user !== true) return;
    let cancelled = false;

    const load = async () => {
      try {
        const r = await api.get("/admin/stats");
        if (cancelled) return;
        setStats(r.data);
        // toast when counts INCREASE compared to last known (but not on very first load)
        if (!firstLoadRef.current) {
          const dApp = r.data.applications_new - lastRef.current.applications_new;
          const dNews = r.data.newsletter_new - lastRef.current.newsletter_new;
          if (dApp > 0) toast.success(`${dApp} nova${dApp > 1 ? "s" : ""} candidatura${dApp > 1 ? "s" : ""}`);
          if (dNews > 0) toast.success(`${dNews} nova${dNews > 1 ? "s" : ""} subscrição${dNews > 1 ? "ões" : ""} à newsletter`);
        }
        lastRef.current = r.data;
        firstLoadRef.current = false;
      } catch (e) {
        // silent
      }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user]);

  // When admin lands on /painel/newsletter, mark all as seen so the badge clears
  useEffect(() => {
    if (user !== true) return;
    if (location.pathname === "/painel/newsletter" && stats.newsletter_new > 0) {
      api.post("/admin/newsletter/mark-seen").then(() => {
        setStats((s) => ({ ...s, newsletter_new: 0 }));
        lastRef.current = { ...lastRef.current, newsletter_new: 0 };
      }).catch(() => {});
    }
  }, [location.pathname, user, stats.newsletter_new]);

  if (user === undefined)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  if (user === false) return <Navigate to="/painel/login" replace />;

  const tab = ({ isActive }) =>
    `relative rounded-full px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
      isActive ? "bg-gold text-black" : "text-white/60 hover:text-white"
    }`;

  return (
    <div data-testid="admin-layout" className="admin-dark min-h-screen bg-[#0a0a0a] text-white">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-black/85 backdrop-blur-xl">
        <div className="mx-auto flex h-auto max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-3">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-8" />
            <span className="font-display text-lg text-white">Painel de Administração</span>
          </div>
          <nav className="flex items-center gap-2">
            <NavLink to="/painel" end className={tab} data-testid="admin-tab-therapists">
              Terapeutas
            </NavLink>
            <NavLink to="/painel/massagens" className={tab} data-testid="admin-tab-massages">
              Massagens
            </NavLink>
            <NavLink to="/painel/candidaturas" className={tab} data-testid="admin-tab-applications">
              <span className="relative inline-block">
                Candidaturas
                <Badge n={stats.applications_new} />
              </span>
            </NavLink>
            <NavLink to="/painel/newsletter" className={tab} data-testid="admin-tab-newsletter">
              <span className="relative inline-block">
                Newsletter
                <Badge n={stats.newsletter_new} />
              </span>
            </NavLink>
            <NavLink to="/painel/espaco" className={tab} data-testid="admin-tab-space">
              Espaço
            </NavLink>
            <NavLink to="/painel/banners" className={tab} data-testid="admin-tab-banners">
              Banners
            </NavLink>
          </nav>
          <div className="flex items-center gap-5">
            <Link
              to="/"
              data-testid="admin-view-site"
              className="flex items-center gap-1.5 text-xs text-white/50 transition-colors hover:text-white"
            >
              <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
              Ver site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 text-xs text-white/50 transition-colors hover:text-gold"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
              Sair
            </button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-5 py-10">
        <Outlet />
      </div>
    </div>
  );
}
